import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  AlertTriangle, Shield, Target, Zap, BarChart2,
  ChevronRight, ArrowUpRight,
} from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

function scoreColor(score) {
  if (score == null) return '#6B7280';
  if (score >= 70) return '#16C784';
  if (score >= 45) return '#F59E0B';
  return '#EF4444';
}

function ScoreRing({ score, size = 110 }) {
  const color = scoreColor(score);
  const r = (size - 12) / 2;
  const circ = 2 * Math.PI * r;
  const fill = ((score || 0) / 100) * circ;
  return (
    <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#1F2937" strokeWidth={10} />
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={10}
        strokeDasharray={`${fill} ${circ}`} strokeLinecap="round"
        style={{ transition: 'stroke-dasharray 0.8s ease' }} />
    </svg>
  );
}

function MiniBar({ score, colors }) {
  return (
    <div className="w-full rounded-full overflow-hidden" style={{ height: 4, backgroundColor: colors.progressTrack || '#1F2937' }}>
      <div className="h-full rounded-full transition-all duration-700"
        style={{ width: `${Math.min(score || 0, 100)}%`, backgroundColor: scoreColor(score) }} />
    </div>
  );
}

function fmtPnl(v) {
  if (v == null) return '—';
  const abs = Math.abs(v);
  const s = abs >= 100000 ? `₹${(abs / 100000).toFixed(1)}L`
    : abs >= 1000 ? `₹${(abs / 1000).toFixed(1)}K`
    : `₹${Math.round(abs)}`;
  return v < 0 ? `-${s}` : `+${s}`;
}

export default function TradePerformancePage() {
  const { colors } = useTheme();
  const navigate = useNavigate();

  const [bh, setBh] = useState(null);
  const [res, setRes] = useState(null);
  const [prep, setPrep] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    Promise.allSettled([
      axios.get(`${API_URL}/behavioral-health`),
      axios.get(`${API_URL}/results/summary`),
      axios.get(`${API_URL}/preparation/summary`),
    ])
      .then(([bhRes, resRes, prepRes]) => {
        if (!cancelled) {
          if (bhRes.status === 'fulfilled') setBh(bhRes.value.data);
          if (resRes.status === 'fulfilled') setRes(resRes.value.data);
          if (prepRes.status === 'fulfilled') setPrep(prepRes.value.data);
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (loading) return <LoadingSkeleton colors={colors} />;
  if (!bh) return (
    <div className="flex items-center justify-center py-20">
      <p className="text-sm" style={{ color: colors.secondaryText }}>Could not load data.</p>
    </div>
  );

  const {
    final_score, discipline_score, personality, personality_state,
    personality_confidence, personality_signals,
    primary_issue, todays_work, risk, breakdown,
  } = bh;

  const isPreLabel = personality_state === 'insufficient' || personality_state === 'mixed';
  const mainColor = scoreColor(final_score);

  // Risk pillar
  const riskScore    = risk?.profile?.risk_score ?? 0;
  const riskAdherence = risk?.adherence?.score ?? 0;
  const riskType     = risk?.profile?.risk_profile_type ?? '—';
  const cleanRate    = risk?.adherence?.clean_rate_pct ?? 0;
  const violations   = risk?.adherence?.violations ?? 0;

  // Results pillar
  const winRate     = res?.summary?.win_rate ?? 0;
  const totalPnl    = res?.summary?.total_pnl ?? null;
  const avgRR       = res?.analytics?.avg_rr ?? 0;
  const maxDrawdown = res?.analytics?.max_drawdown ?? 0;

  // Preparation pillar
  const readiness       = prep?.readiness ?? {};
  const decisionQuality = prep?.decision_quality ?? {};

  return (
    <div className="space-y-4 pb-6">

      {/* ── Unified Core Score ────────────────────────────────────────── */}
      <div className="app-card rounded-xl p-5"
        style={{ background: `linear-gradient(135deg, ${colors.accent}20 0%, ${colors.accent}08 100%)`, border: `1px solid ${mainColor}50`, boxShadow: `0 0 24px ${mainColor}25` }}>
        <div className="flex items-center gap-5">
          <div className="relative flex-shrink-0" style={{ width: 110, height: 110 }}>
            <ScoreRing score={final_score} size={110} />
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-2xl font-bold leading-none" style={{ color: mainColor }}>{final_score}</span>
              <span className="text-[13px] mt-0.5" style={{ color: colors.secondaryText }}>/100</span>
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold uppercase tracking-wider mb-1" style={{ color: colors.secondaryText }}>
              Overall Performance
            </p>
            <p className="text-lg font-bold leading-tight" style={{ color: mainColor }}>{personality}</p>
            <p className="text-xs mt-0.5 mb-3" style={{ color: colors.secondaryText }}>
              Behavior · Risk · Results · Preparation
            </p>
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg"
              style={{ backgroundColor: '#EF444415', border: '1px solid #EF444430' }}>
              <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" style={{ color: '#EF4444' }} />
              <div className="min-w-0">
                <p className="text-[11px] font-semibold" style={{ color: '#EF4444' }}>Primary Issue</p>
                <p className="text-xs font-bold truncate" style={{ color: colors.primaryText }}>{primary_issue}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Behavioral Signals ───────────────────────────────────────── */}
      {personality_signals?.length > 0 && (
        <div className="app-card rounded-xl p-4" style={{ background: `linear-gradient(135deg, ${colors.accent}20 0%, ${colors.accent}08 100%)`, border: `1px solid ${colors.accentBorder}` }}>
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: colors.secondaryText }}>
              Why {personality}
            </p>
            <span className="text-[11px] px-1.5 py-0.5 rounded-full font-medium"
              style={{ backgroundColor: `${mainColor}15`, color: mainColor }}>
              {!isPreLabel && personality_confidence > 0 ? `${personality_confidence}% confidence` : 'Observed patterns'}
            </span>
          </div>
          <div className="space-y-2">
            {personality_signals.map((sig, i) => (
              <div key={i} className="flex items-start gap-2.5 px-3 py-2 rounded-lg"
                style={{ backgroundColor: colors.inputBg }}>
                <div className="w-1.5 h-1.5 rounded-full flex-shrink-0 mt-1.5" style={{ backgroundColor: mainColor }} />
                <p className="text-[13px] leading-snug" style={{ color: colors.primaryText }}>{sig}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Today's Work ──────────────────────────────────────────────── */}
      {todays_work?.length > 0 && (
        <div className="app-card rounded-xl p-4" style={{ background: `linear-gradient(135deg, ${colors.accent}20 0%, ${colors.accent}08 100%)`, border: `1px solid ${colors.accentBorder}` }}>
          <p className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: colors.secondaryText }}>
            Today's Work
          </p>
          <div className="space-y-2">
            {todays_work.map((instruction, i) => (
              <div key={i} className="flex items-start gap-3 px-3 py-2.5 rounded-lg"
                style={{ backgroundColor: colors.inputBg, borderLeft: `3px solid ${i === 0 ? '#EF4444' : '#F59E0B'}` }}>
                <span className="text-xs font-bold mt-0.5 flex-shrink-0"
                  style={{ color: i === 0 ? '#EF4444' : '#F59E0B' }}>{i + 1}</span>
                <p className="text-xs leading-relaxed font-medium" style={{ color: colors.primaryText }}>{instruction}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── 4 Pillar Cards ────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 gap-3">

        {/* BEHAVIOR */}
        <div className="app-card rounded-xl p-3 cursor-pointer flex flex-col"
          style={{ background: `linear-gradient(135deg, ${colors.accent}20 0%, ${colors.accent}08 100%)`, border: `1px solid ${colors.accentBorder}` }}
          onClick={() => navigate('/discipline')}>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-1.5">
              <Shield className="w-3.5 h-3.5" style={{ color: scoreColor(discipline_score) }} />
              <span className="text-[13px] font-semibold" style={{ color: colors.primaryText }}>Behavior</span>
            </div>
            <ArrowUpRight className="w-3 h-3" style={{ color: colors.secondaryText }} />
          </div>
          <div className="flex items-baseline gap-1 mb-2.5">
            <span className="text-2xl font-bold tabular-nums leading-none" style={{ color: scoreColor(discipline_score) }}>
              {discipline_score}
            </span>
            <span className="text-[12px]" style={{ color: colors.secondaryText }}>/100</span>
          </div>
          <div className="flex-1 space-y-1.5">
            {[
              { label: 'Stop Loss',      value: breakdown?.stop_loss ?? 0 },
              { label: 'Emotion',        value: breakdown?.emotion ?? 0 },
              { label: 'Strategy',       value: breakdown?.strategy ?? 0 },
            ].map(({ label, value }) => (
              <div key={label}>
                <div className="flex items-center justify-between mb-0.5">
                  <span className="text-[12px]" style={{ color: colors.secondaryText }}>{label}</span>
                  <span className="text-[12px] font-semibold" style={{ color: scoreColor(value) }}>{value}</span>
                </div>
                <MiniBar score={value} colors={colors} />
              </div>
            ))}
          </div>
          <div className="mt-2 pt-2 flex items-center justify-between" style={{ borderTop: `1px solid ${colors.border}` }}>
            <span className="text-[12px]" style={{ color: colors.secondaryText }}>Full Report</span>
            <ChevronRight className="w-3 h-3" style={{ color: colors.secondaryText }} />
          </div>
        </div>

        {/* RISK */}
        <div className="app-card rounded-xl p-3 cursor-pointer flex flex-col"
          style={{ background: `linear-gradient(135deg, ${colors.accent}20 0%, ${colors.accent}08 100%)`, border: `1px solid ${colors.accentBorder}` }}
          onClick={() => navigate('/profile?section=risk-profile')}>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-1.5">
              <Target className="w-3.5 h-3.5" style={{ color: scoreColor(riskAdherence) }} />
              <span className="text-[13px] font-semibold" style={{ color: colors.primaryText }}>Risk</span>
            </div>
            <ArrowUpRight className="w-3 h-3" style={{ color: colors.secondaryText }} />
          </div>
          <div className="flex items-baseline gap-1 mb-1">
            <span className="text-2xl font-bold tabular-nums leading-none" style={{ color: scoreColor(riskAdherence) }}>
              {riskAdherence}
            </span>
            <span className="text-[12px]" style={{ color: colors.secondaryText }}>/100</span>
          </div>
          <div className="mb-2">
            <span className="text-[11px] px-1.5 py-0.5 rounded-full font-medium"
              style={{ backgroundColor: `${scoreColor(riskAdherence)}18`, color: scoreColor(riskAdherence) }}>
              {riskType}
            </span>
          </div>
          <div className="flex-1 space-y-1.5">
            <div>
              <div className="flex items-center justify-between mb-0.5">
                <span className="text-[12px]" style={{ color: colors.secondaryText }}>Profile Score</span>
                <span className="text-[12px] font-semibold" style={{ color: scoreColor(riskScore) }}>{riskScore}</span>
              </div>
              <MiniBar score={riskScore} colors={colors} />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-[12px]" style={{ color: colors.secondaryText }}>Clean trades</span>
              <span className="text-[12px] font-semibold"
                style={{ color: cleanRate >= 80 ? '#16C784' : cleanRate >= 60 ? '#F59E0B' : '#EF4444' }}>
                {cleanRate}%
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-[12px]" style={{ color: colors.secondaryText }}>Violations</span>
              <span className="text-[12px] font-semibold" style={{ color: violations > 0 ? '#EF4444' : '#16C784' }}>
                {violations}
              </span>
            </div>
          </div>
          <div className="mt-2 pt-2 flex items-center justify-between" style={{ borderTop: `1px solid ${colors.border}` }}>
            <span className="text-[12px]" style={{ color: colors.secondaryText }}>Risk Profile</span>
            <ChevronRight className="w-3 h-3" style={{ color: colors.secondaryText }} />
          </div>
        </div>

        {/* RESULTS */}
        <div className="app-card rounded-xl p-3 cursor-pointer flex flex-col"
          style={{ background: `linear-gradient(135deg, ${colors.accent}20 0%, ${colors.accent}08 100%)`, border: `1px solid ${colors.accentBorder}` }}
          onClick={() => navigate('/trading-analytics')}>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-1.5">
              <BarChart2 className="w-3.5 h-3.5" style={{ color: scoreColor(winRate) }} />
              <span className="text-[13px] font-semibold" style={{ color: colors.primaryText }}>Results</span>
            </div>
            <ArrowUpRight className="w-3 h-3" style={{ color: colors.secondaryText }} />
          </div>
          <div className="flex items-baseline gap-1 mb-2.5">
            <span className="text-2xl font-bold tabular-nums leading-none" style={{ color: scoreColor(winRate) }}>
              {winRate}%
            </span>
            <span className="text-[12px]" style={{ color: colors.secondaryText }}>win rate</span>
          </div>
          <div className="flex-1 space-y-1.5">
            <div className="flex items-center justify-between">
              <span className="text-[12px]" style={{ color: colors.secondaryText }}>Total P&L</span>
              <span className="text-[12px] font-semibold"
                style={{ color: (totalPnl ?? 0) >= 0 ? '#16C784' : '#EF4444' }}>
                {fmtPnl(totalPnl)}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-[12px]" style={{ color: colors.secondaryText }}>Avg R:R</span>
              <span className="text-[12px] font-semibold"
                style={{ color: avgRR >= 2 ? '#16C784' : avgRR >= 1 ? '#F59E0B' : '#EF4444' }}>
                {avgRR > 0 ? `${avgRR}x` : '—'}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-[12px]" style={{ color: colors.secondaryText }}>Max Drawdown</span>
              <span className="text-[12px] font-semibold" style={{ color: '#EF4444' }}>
                {maxDrawdown > 0 ? fmtPnl(-maxDrawdown) : '—'}
              </span>
            </div>
          </div>
          <div className="mt-2 pt-2 flex items-center justify-between" style={{ borderTop: `1px solid ${colors.border}` }}>
            <span className="text-[12px]" style={{ color: colors.secondaryText }}>Trade Analysis</span>
            <ChevronRight className="w-3 h-3" style={{ color: colors.secondaryText }} />
          </div>
        </div>

        {/* PREPARATION */}
        <div className="app-card rounded-xl p-3 cursor-pointer flex flex-col"
          style={{ background: `linear-gradient(135deg, ${colors.accent}20 0%, ${colors.accent}08 100%)`, border: `1px solid ${colors.accentBorder}` }}
          onClick={() => navigate('/act')}>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-1.5">
              <Zap className="w-3.5 h-3.5" style={{ color: scoreColor(readiness.score) }} />
              <span className="text-[13px] font-semibold" style={{ color: colors.primaryText }}>Preparation</span>
            </div>
            <ArrowUpRight className="w-3 h-3" style={{ color: colors.secondaryText }} />
          </div>
          <div className="flex items-baseline gap-1 mb-1">
            <span className="text-2xl font-bold tabular-nums leading-none" style={{ color: scoreColor(readiness.score) }}>
              {readiness.score ?? '—'}
            </span>
            {readiness.score != null && (
              <span className="text-[12px]" style={{ color: colors.secondaryText }}>/100</span>
            )}
          </div>
          <div className="mb-2">
            {readiness.label && (
              <span className="text-[11px] px-1.5 py-0.5 rounded-full font-medium"
                style={{ backgroundColor: `${scoreColor(readiness.score)}18`, color: scoreColor(readiness.score) }}>
                {readiness.label}
              </span>
            )}
          </div>
          <div className="flex-1 space-y-1.5">
            {readiness.score != null && (
              <div>
                <div className="flex items-center justify-between mb-0.5">
                  <span className="text-[12px]" style={{ color: colors.secondaryText }}>Readiness</span>
                  <span className="text-[12px] font-semibold" style={{ color: scoreColor(readiness.score) }}>
                    {readiness.score}
                  </span>
                </div>
                <MiniBar score={readiness.score} colors={colors} />
              </div>
            )}
            <div className="flex items-center justify-between">
              <span className="text-[12px]" style={{ color: colors.secondaryText }}>ACT Sessions</span>
              <span className="text-[12px] font-semibold" style={{ color: colors.primaryText }}>
                {readiness.count ?? 0}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-[12px]" style={{ color: colors.secondaryText }}>Decision Quality</span>
              <span className="text-[12px] font-semibold"
                style={{ color: scoreColor(decisionQuality.avg > 0 ? decisionQuality.avg : null) }}>
                {decisionQuality.avg > 0 ? decisionQuality.avg : '—'}
              </span>
            </div>
          </div>
          <div className="mt-2 pt-2 flex items-center justify-between" style={{ borderTop: `1px solid ${colors.border}` }}>
            <span className="text-[12px]" style={{ color: colors.secondaryText }}>Preparation</span>
            <ChevronRight className="w-3 h-3" style={{ color: colors.secondaryText }} />
          </div>
        </div>

      </div>
    </div>
  );
}

function LoadingSkeleton({ colors }) {
  return (
    <div className="space-y-4 pb-6 animate-pulse">
      {[80, 160, 100].map((h, i) => (
        <div key={i} className="app-card rounded-xl" style={{ height: h, background: `linear-gradient(135deg, ${colors.accent}20 0%, ${colors.accent}08 100%)` }} />
      ))}
      <div className="grid grid-cols-2 gap-3">
        {[1, 2, 3, 4].map(i => (
          <div key={i} className="app-card rounded-xl" style={{ height: 180, background: `linear-gradient(135deg, ${colors.accent}20 0%, ${colors.accent}08 100%)` }} />
        ))}
      </div>
    </div>
  );
}
